import _ from "lodash";
import React from "react";
import { shallowEqual } from "react-redux";
import i18n from "src/i18n/config";
import { RootState, useSelector } from "src/store";
import { z } from "zod";

import { selectDevicesByGroupIds } from "@dashboard/devices/store/selectors/devices";
import { Device } from "@dashboard/devices/types";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";

import { AddedDeviceType } from "./AddedDevice";

const qrCodeSchema = z.object({
    serial: z.string(),
});

export const parseQrCodeValue = (rawValue: string): string => {
    const trimmed = rawValue.trim();
    try {
        const parsed = JSON.parse(trimmed);
        const result = qrCodeSchema.parse(parsed);
        return result.serial;
    } catch {
        return trimmed;
    }
};

export const schema = z.object({
    androidSerial: z
        .string({ message: i18n.t("common:validation.fieldRequired") })
        .refine(
            (value) => {
                const serialNumbers = value
                    .split(",")
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
                if (serialNumbers.length === 0) {
                    return false;
                }
                return serialNumbers.every((serial) => {
                    return serial.length >= 8 && /^[a-zA-Z0-9]+$/.test(serial);
                });
            },
            { message: i18n.t("common:validation.deviceSerialAlphanumeric") },
        )
        .refine(
            (value) => {
                const serialNumbers = value
                    .split(",")
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
                if (serialNumbers.length === 0) {
                    return false;
                }
                return serialNumbers.every((serial) => serial.length >= 8);
            },
            { message: i18n.t("common:validation.deviceSerialMinLength") },
        ),
    productName: z.string({ message: i18n.t("common:validation.fieldRequired") }),
    groupId: z.number({ message: i18n.t("common:validation.fieldRequired") }),
});

export const useDevices = () => {
    const { data: currentSubscription } = useCurrentSubscription({});
    const groupIds = currentSubscription?.groupIds || [];
    const storeDevices = useSelector((state: RootState) => selectDevicesByGroupIds(state, groupIds), shallowEqual);
    const devices = React.useRef<Device[]>(storeDevices);

    const [addedDevices, setAddedDevices] = React.useReducer((state: AddedDeviceType[], action: AddedDeviceType[]) => {
        const sorted = _.orderBy([...state, ...action], "createdAt", ["desc"]);
        devices.current = storeDevices;
        return sorted;
    }, []);

    return { addedDevices, setAddedDevices, devices };
};
